import { useUser } from "@dashboard/auth/useUser";
import { useExitGuard } from "@dashboard/components/Form/useExitGuard";
import {
  useAtticAssetDeleteMutation,
  useAtticAssetUploadConfirmMutation,
  useAtticAssetUploadCreateMutation,
} from "@dashboard/graphql";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { type IntlShape, useIntl } from "react-intl";

import { type AtticAssetHaltReason, callAtticAssetMutation } from "../callOutcome";
import { getAtticAssetErrorMessage } from "../errors";
import { uploadMessages } from "../messages";
import {
  type AtticAssetPut,
  type AtticAssetUploadFailure,
  type AtticAssetUploadResult,
  type AtticAssetUploadStage,
  uploadAtticAsset,
  validateAtticAssetUpload,
} from "../upload";
import {
  type AtticAssetUploadEntry,
  createRefreshThrottle,
  formatBytes,
  getAtticAssetUploadKey,
} from "../utils";

export type AtticAssetUploadStatus =
  | "queued"
  | AtticAssetUploadStage
  | "done"
  | "failed"
  | "cancelled";

export interface AtticAssetUploadItem {
  id: number;
  filename: string;
  /** The folders inside the directory the file goes to; empty for the root. */
  subpath: string;
  size: number;
  status: AtticAssetUploadStatus;
  /** Share of the PUT sent, 0-100, while uploading. */
  progress?: number;
  error?: string;
}

/** Statuses a merchant may still cancel: nothing has reached confirm yet. */
export const isCancellableUpload = (status: AtticAssetUploadStatus) =>
  status === "queued" || status === "create" || status === "put";

/** Statuses that are over, which "Clear finished" removes. */
export const isFinishedUpload = (status: AtticAssetUploadStatus) =>
  status === "done" || status === "failed" || status === "cancelled";

/** At most this many files are in flight at once, across every drop. */
export const UPLOAD_CONCURRENCY = 3;

/** The file list is refreshed at most this often while files go live, and once at the end. */
export const REFRESH_INTERVAL_MS = 3_000;

/** Nothing of the file reached storage: it can simply be uploaded again. */
const haltMessages: Record<AtticAssetHaltReason, typeof uploadMessages.sessionExpired> = {
  sessionExpired: uploadMessages.sessionExpired,
  readOnly: uploadMessages.readOnly,
};

/** The bytes landed first: the name stays held until the backend clears the pending upload. */
const haltAfterUploadMessages: Record<AtticAssetHaltReason, typeof uploadMessages.sessionExpired> =
  {
    sessionExpired: uploadMessages.sessionExpiredAfterUpload,
    readOnly: uploadMessages.readOnlyAfterUpload,
  };

/** A file that is in storage but not confirmed: what happened, then why the name is held. */
const withNameHeld = (intl: IntlShape, what: string) =>
  intl.formatMessage(uploadMessages.withNameHeld, {
    what,
    nameHeld: intl.formatMessage(uploadMessages.nameHeld),
  });

/**
 * The sentence a failed upload row shows: what the browser refused, what the
 * queue knows (session, lost answers, stalls, a name raced), the merchant
 * wording for the backend's code (errors.ts), or what failed at which stage.
 */
export const describeUploadFailure = (result: AtticAssetUploadFailure, intl: IntlShape): string => {
  if (result.stage === "validate") {
    switch (result.refusal.reason) {
      case "type":
        return intl.formatMessage(uploadMessages.refusedType);
      case "empty":
        return intl.formatMessage(uploadMessages.refusedEmpty);
      case "tooLarge":
        return intl.formatMessage(uploadMessages.refusedTooLarge, {
          max: formatBytes(result.refusal.maxBytes, intl.locale),
        });
      case "folder":
        return intl.formatMessage(uploadMessages.refusedFolder);
    }
  }

  switch (result.reason) {
    case "sessionExpired":
    case "readOnly":
      return result.stage === "confirm"
        ? withNameHeld(intl, intl.formatMessage(haltAfterUploadMessages[result.reason]))
        : intl.formatMessage(haltMessages[result.reason]);
    case "outcomeUnknown":
      return intl.formatMessage(uploadMessages.outcomeUnknown);
    case "unconfirmed":
      return withNameHeld(intl, intl.formatMessage(uploadMessages.unconfirmed));
    case "refusedAfterUpload":
      return withNameHeld(
        intl,
        result.code || result.message
          ? getAtticAssetErrorMessage(result, intl, "file")
          : intl.formatMessage(uploadMessages.confirmFailed),
      );
    case "createUnanswered":
      return intl.formatMessage(uploadMessages.createUnanswered);
    case "stalled":
      return intl.formatMessage(uploadMessages.stalled);
  }

  if (result.code || result.message) {
    return getAtticAssetErrorMessage(result, intl, "file");
  }

  switch (result.stage) {
    case "create":
      return intl.formatMessage(uploadMessages.createFailed);
    case "put":
      return result.status
        ? intl.formatMessage(uploadMessages.putRefused, { status: result.status })
        : intl.formatMessage(uploadMessages.putFailed);
    default:
      return intl.formatMessage(uploadMessages.confirmFailed);
  }
};

interface UseAtticAssetUploadsOptions {
  directoryId: string;
  maxUploadBytes: number;
  /**
   * Refresh the file list: called as files go live (or may have), at most
   * every REFRESH_INTERVAL_MS, and once more when the queue empties.
   */
  onUploaded: () => void;
  /** The bucket PUT and the confirm-retry wait; tests stand in for the network here. */
  transport?: { put?: AtticAssetPut; wait?: (ms: number) => Promise<void> };
}

interface Job {
  id: number;
  file: File;
  subpath: string;
  /** The key inside the directory this upload claims in the queue, while it is unsettled. */
  key: string | null;
  directoryId: string;
  maxUploadBytes: number;
  /** The drop generation it was queued in (see `generation`). */
  generation: number;
  controller: AbortController;
  /** The last percent shown, so a progress event that does not move it renders nothing. */
  percent?: number;
}

const unclaim = (claimed: Map<string, number>, job: Job) => {
  if (job.key !== null && claimed.get(job.key) === job.id) {
    claimed.delete(job.key);
  }
};

/**
 * The upload queue for one directory view. Files run UPLOAD_CONCURRENCY at a
 * time in the order they were dropped, so one slow PUT does not hold up the
 * rest; the backend checks the quota at each reservation, so parallel
 * uploads cannot overrun it. Each row can be cancelled until confirm starts,
 * and a PUT with no progress for a minute is abandoned. Each upload asks for
 * its signed URL only when its slot is free, right before its PUT.
 *
 * Two files that would get the same key (`My Photo.png` and `my-photo.png`,
 * folders that clean to one name, a file dropped again mid-upload) never run
 * at once: while one is queued, running or not yet settled, a later one is
 * refused on its row at once, without a call to the API. Both racing for one
 * object is how a release could remove the file that went live.
 *
 * Every create and confirm refusal, and every call that got no answer, is
 * said on its row and nowhere else: the mutation wrapper's toasts are off for
 * those two. What the wrapper did that a row cannot is kept here: an expired
 * session logs out with one notice and stops the queue, and read-only mode
 * gives one notice and stops it too.
 *
 * Leaving while anything is queued or in flight asks first, through the
 * Dashboard's exit-dialog provider: its dialog for an in-app link, the
 * browser's own prompt for a reload or tab close (the provider's
 * beforeunload). The dialog's wording is the provider's and cannot be
 * changed per form, so the card says while uploads run that leaving cancels
 * them (`inFlight`). Leaving anyway aborts what is in flight (each aborted
 * upload releases its own reservation) and drops the queue.
 */
export const useAtticAssetUploads = ({
  directoryId,
  maxUploadBytes,
  onUploaded,
  transport,
}: UseAtticAssetUploadsOptions) => {
  const intl = useIntl();
  const notify = useNotifier();
  const user = useUser();
  const [items, setItems] = useState<AtticAssetUploadItem[]>([]);
  const nextId = useRef(0);
  const pending = useRef<Job[]>([]);
  const active = useRef(new Map<number, Job>());
  // Key inside the directory -> the job holding it, from enqueue until it settles.
  const claimed = useRef(new Map<string, number>());
  const mounted = useRef(true);
  // Set by the first session-expired or read-only answer; a new drop clears it.
  const halted = useRef<AtticAssetHaltReason | null>(null);
  // Moves on with each drop that clears a halt. Only a job of the current
  // generation can halt the queue: one still in flight from before the halt
  // that answers the same way says nothing new, and must not log out twice or
  // stop the drop that came after it.
  const generation = useRef(0);
  const [createUpload] = useAtticAssetUploadCreateMutation({ disableErrorHandling: true });
  const [confirmUpload] = useAtticAssetUploadConfirmMutation({ disableErrorHandling: true });
  // Best effort and silent: the row already says why the upload stopped.
  const [releaseUpload] = useAtticAssetDeleteMutation({ disableErrorHandling: true });
  // The queue outlives renders; it reads the newest callbacks from here.
  const current = {
    intl,
    notify,
    user,
    onUploaded,
    transport,
    createUpload,
    confirmUpload,
    releaseUpload,
  };
  const latest = useRef(current);

  latest.current = current;

  const update = useCallback((ids: number[], patch: Partial<AtticAssetUploadItem>) => {
    if (mounted.current) {
      setItems(current =>
        current.map(item => (ids.includes(item.id) ? { ...item, ...patch } : item)),
      );
    }
  }, []);

  // Files going live refresh the list as they land, throttled, and once more
  // when the queue empties: a long batch shows progress, a burst costs one refetch.
  const refresh = useRef<ReturnType<typeof createRefreshThrottle> | null>(null);

  if (!refresh.current) {
    refresh.current = createRefreshThrottle(() => {
      if (mounted.current) {
        latest.current.onUploaded();
      }
    }, REFRESH_INTERVAL_MS);
  }

  const throttle = refresh.current;

  const halt = useCallback(
    (reason: AtticAssetHaltReason) => {
      if (halted.current) {
        return;
      }

      halted.current = reason;

      const { notify, intl, user } = latest.current;
      const stopped = pending.current.map(job => job.id);

      pending.current.forEach(job => unclaim(claimed.current, job));
      pending.current = [];
      update(stopped, {
        status: "failed",
        error: intl.formatMessage(haltMessages[reason]),
      });

      if (reason === "sessionExpired") {
        user.logout?.();
        notify({ status: "error", text: intl.formatMessage(commonMessages.sessionExpired) });
      } else {
        notify({ status: "error", text: intl.formatMessage(commonMessages.readOnly) });
      }
    },
    [update],
  );

  const settle = useCallback(
    (job: Job, result: AtticAssetUploadResult<unknown>) => {
      unclaim(claimed.current, job);

      if (result.ok === true) {
        update([job.id], { status: "done", progress: undefined });
        throttle.request();
      } else if (result.stage !== "validate" && result.reason === "cancelled") {
        update([job.id], { status: "cancelled", progress: undefined });
      } else {
        update([job.id], {
          status: "failed",
          progress: undefined,
          error: describeUploadFailure(result, latest.current.intl),
        });

        if (result.stage !== "validate") {
          if (result.reason === "sessionExpired" || result.reason === "readOnly") {
            if (job.generation === generation.current) {
              halt(result.reason);
            }
          } else if (result.reason === "outcomeUnknown" || result.reason === "unconfirmed") {
            // It may have gone live: the list shows whether it did.
            throttle.request();
          }
        }
      }
    },
    [halt, throttle, update],
  );

  const pump = useCallback(() => {
    while (active.current.size < UPLOAD_CONCURRENCY && pending.current.length) {
      const job = pending.current.shift() as Job;

      active.current.set(job.id, job);

      uploadAtticAsset<unknown>(
        {
          file: job.file,
          directoryId: job.directoryId,
          subpath: job.subpath,
          maxUploadBytes: job.maxUploadBytes,
        },
        {
          // Each call carries the abort signal of its timeout to fetch
          // (apollo-upload-client honours `context.fetchOptions.signal`).
          createUpload: (input, signal) =>
            callAtticAssetMutation(
              () =>
                latest.current.createUpload({
                  variables: { input },
                  context: { fetchOptions: { signal } },
                }),
              data => data.atticAssetUploadCreate,
            ),
          confirmUpload: (key, signal) =>
            callAtticAssetMutation(
              () =>
                latest.current.confirmUpload({
                  variables: { key },
                  context: { fetchOptions: { signal } },
                }),
              data => data.atticAssetUploadConfirm,
            ),
          releaseUpload: (id, signal) =>
            latest.current.releaseUpload({
              variables: { id },
              context: { fetchOptions: { signal } },
            }),
          put: latest.current.transport?.put,
          wait: latest.current.transport?.wait,
          signal: job.controller.signal,
          onStage: stage => update([job.id], { status: stage }),
          onProgress: fraction => {
            const percent = Math.round(fraction * 100);

            if (percent !== job.percent) {
              job.percent = percent;
              update([job.id], { progress: percent });
            }
          },
        },
      )
        .catch(
          (): AtticAssetUploadResult<unknown> => ({
            ok: false,
            stage: "create",
            message: null,
          }),
        )
        .then(result => {
          active.current.delete(job.id);
          settle(job, result);

          if (!mounted.current) {
            return;
          }

          pump();

          if (!active.current.size && !pending.current.length) {
            // The queue is empty: the last refresh goes out now, not on the timer.
            throttle.flush();
          }
        });
    }
  }, [settle, throttle, update]);

  const upload = useCallback(
    (entries: AtticAssetUploadEntry[]) => {
      if (!entries.length) {
        return;
      }

      // A new drop after a halt is the merchant trying again (signed in again,
      // read-only lifted): it gets its own chance and, if needed, its own notice.
      if (halted.current) {
        halted.current = null;
        generation.current += 1;
      }

      const added: AtticAssetUploadItem[] = [];
      const jobs: Job[] = [];

      const { intl } = latest.current;

      entries.forEach(({ file, subpath, unusableFolder }) => {
        const id = nextId.current++;
        const row = { id, filename: file.name, subpath, size: file.size };
        const refuse = (error: string) => added.push({ ...row, status: "failed", error });

        if (unusableFolder !== undefined) {
          refuse(intl.formatMessage(uploadMessages.refusedFolderName, { folder: unusableFolder }));

          return;
        }

        // Checked now, so a file the browser refuses says so at once instead
        // of sitting on "Waiting" until a slot frees.
        const refusal = validateAtticAssetUpload({ file, subpath, maxUploadBytes });

        if (refusal) {
          refuse(describeUploadFailure({ ok: false, stage: "validate", refusal }, intl));

          return;
        }

        const key = getAtticAssetUploadKey(subpath, file.name);

        if (key !== null && claimed.current.has(key)) {
          refuse(intl.formatMessage(uploadMessages.duplicateInQueue));

          return;
        }

        if (key !== null) {
          claimed.current.set(key, id);
        }

        added.push({ ...row, status: "queued" });
        jobs.push({
          id,
          file,
          subpath,
          key,
          directoryId,
          maxUploadBytes,
          generation: generation.current,
          controller: new AbortController(),
        });
      });

      setItems(current => [...current, ...added]);
      pending.current.push(...jobs);
      pump();
    },
    [directoryId, maxUploadBytes, pump],
  );

  const cancel = useCallback(
    (id: number) => {
      const queuedIndex = pending.current.findIndex(job => job.id === id);

      if (queuedIndex >= 0) {
        // Never started: no reservation to release.
        const [job] = pending.current.splice(queuedIndex, 1);

        settle(job, { ok: false, stage: "create", message: null, reason: "cancelled" });

        if (!active.current.size && !pending.current.length) {
          throttle.flush();
        }

        return;
      }

      // In flight: uploadAtticAsset stops and releases the reservation; once
      // confirm has started it ignores the abort.
      active.current.get(id)?.controller.abort();
    },
    [settle, throttle],
  );

  const clearFinished = useCallback(
    () => setItems(current => current.filter(item => !isFinishedUpload(item.status))),
    [],
  );

  const inFlight = items.some(item => !isFinishedUpload(item.status));

  // In-app navigation asks first through the Dashboard's exit dialog; the
  // provider's own beforeunload listener covers a reload or tab close.
  useExitGuard(inFlight);

  useEffect(() => {
    // The Map is never replaced, only mutated: the cleanup sees what is in flight then.
    const inFlightJobs = active.current;
    const claimedKeys = claimed.current;

    mounted.current = true;

    return () => {
      // Leaving the view: drop what never started, abort what did (each
      // aborted upload releases its own reservation).
      mounted.current = false;
      pending.current = [];
      claimedKeys.clear();
      inFlightJobs.forEach(job => job.controller.abort());
      throttle.cancel();
    };
  }, [throttle]);

  return { items, inFlight, upload, cancel, clearFinished };
};
